import React, { useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "../i18n/I18nProvider";
import { QuoteMemberFormState } from "../types/leadWizard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";

interface QuoteMembersStepProps {
  initialMembers: QuoteMemberFormState[];
  onSubmit: (members: QuoteMemberFormState[]) => Promise<void>;
  onBack: () => void;
  isSubmitting: boolean;
  selectedHouseholdTypeIcon?: string | null;
}

const RELATIONSHIP_OPTIONS = [
  "Self",
  "Spouse",
  "Child",
  "Brother or Sister",
  "Foster Child",
  "Grandson or Granddaughter",
  "Nephew or Niece",
  "Ward",
  "Adopted Child",
  "Court Appointed Guardian",
  "Dependent of a Minor Dependent",
  "Stepson or Stepdaughter",
  "Other Relationship",
  "Other Relative",
];

export const QuoteMembersStep: React.FC<QuoteMembersStepProps> = ({
  initialMembers,
  onSubmit,
  onBack,
  selectedHouseholdTypeIcon,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const [members, setMembers] =
    useState<QuoteMemberFormState[]>(initialMembers);
  const [error, setError] = useState<string | null>(null);

  const handleMemberChange = (
    index: number,
    field: keyof QuoteMemberFormState,
    value: string | boolean
  ) => {
    setMembers((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return next;
    });
  };

  const validate = (): boolean => {
    for (const member of members) {
      if (!member.relationship) {
        setError(t("quote.members.errors.relationship"));
        return false;
      }
    }
    setError(null);
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    try {
      await onSubmit(members);
    } catch (err: any) {
      setError(err?.message ?? t("wizard.errors.generic"));
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <button
          type="button"
          className="button button-secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          {t("buttons.back")}
        </button>
      </div>
      <div className="card-body">
        <div className="household-type-selection-header">
          <div>
            <h3 className="card-title">{t("quote.members.title")}</h3>
            <p className="section-helper">{t("quote.members.helper")}</p>
          </div>
          {selectedHouseholdTypeIcon && (
            <div className="household-type-selected">
              <span aria-hidden="true" className="household-type-icon">
                {selectedHouseholdTypeIcon}
                <FontAwesomeIcon
                  className="fa-check step-4"
                  icon={faCircleCheck}
                />
              </span>
            </div>
          )}
        </div>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          {members.map((member, index) => (
            <div
              key={`${member.firstName}-${index}`}
              className="quote-member-card"
            >
              <h5 className="member-name">
                {member.firstName} {member.lastName}
              </h5>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId={`memberRelationship-${index}`}>
                    <Form.Label>{t("quote.members.relationship")}</Form.Label>
                    <Form.Select
                      value={member.relationship}
                      onChange={(event) =>
                        handleMemberChange(
                          index,
                          "relationship",
                          event.target.value
                        )
                      }
                    >
                      <option value="">
                        {t("quote.members.relationshipPlaceholder")}
                      </option>
                      {RELATIONSHIP_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId={`memberEmail-${index}`}>
                    <Form.Label>{t("quote.members.email")}</Form.Label>
                    <Form.Control
                      type="email"
                      value={member.email ?? ""}
                      onChange={(event) =>
                        handleMemberChange(index, "email", event.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId={`memberPhone-${index}`}>
                    <Form.Label>{t("quote.members.phone")}</Form.Label>
                    <Form.Control
                      value={member.phone ?? ""}
                      onChange={(event) =>
                        handleMemberChange(index, "phone", event.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Check
                    type="switch"
                    id={`memberTobacco-${index}`}
                    label={t("quote.members.usesTobacco")}
                    checked={Boolean(member.usesTobacco)}
                    onChange={(event) =>
                      handleMemberChange(
                        index,
                        "usesTobacco",
                        event.target.checked
                      )
                    }
                  />
                </Col>
                <Col md={3}>
                  <Form.Check
                    type="switch"
                    id={`memberPregnant-${index}`}
                    label={t("quote.members.isPregnant")}
                    checked={Boolean(member.isPregnant)}
                    onChange={(event) =>
                      handleMemberChange(
                        index,
                        "isPregnant",
                        event.target.checked
                      )
                    }
                  />
                </Col>
              </Row>
            </div>
          ))}

          <div className="form-actions">
            <Button
              type="submit"
              className="button button-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t("quote.members.saving")}
                </>
              ) : (
                t("buttons.continue")
              )}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};
